import {
  Box,
  Divider,
  GridItem,
  HStack,
  SimpleGrid,
  Slider,
  SliderFilledTrack,
  SliderMark,
  SliderThumb,
  SliderTrack,
  VStack,
} from '@chakra-ui/react';
import { Icon } from '@common/components/Icon';
import { Image } from '@common/components/Image';
import { usePropertiesModel } from '@common/models/PropertiesModel';
import { FontFamily } from '@common/theming/Fonts';
import React, { useEffect } from 'react';
import { TextBlock } from 'src/apps/invest/common/components/TextBlock';

import { SectionHeader } from '../common/SectionHeader';
import { HACKED_TOGETHER_PROPERTY_IDS } from '../constants';
import { CustomDetail } from './CustomDetail';

type HistoricalReturnsProps = {
  children?: React.ReactNode;
};

export const HistoricalReturns: React.FC<HistoricalReturnsProps> = () => {
  const { property } = usePropertiesModel();
  const [equity, setEquity] = React.useState(Number(property?.sfr?.market_data.projected_equity_rate ?? 0));
  const [dividend, setDividend] = React.useState(Number(property?.sfr?.market_data.projected_dividend_yield ?? 0));

  // slider settings
  const sliderMax = 100; // max slider val
  // min/max offsets from min/max values to keep values "balanced on chart
  // results in min/max values starting out at approx 15% from left and right
  const sliderRangeCoEfficient = 0.15;

  // Equity slider settings
  const equityNationalAverage = Number(property?.sfr?.market_data.equity_national_average ?? 0);
  const equityHistoricalAverage = Number(property?.sfr?.market_data.equity_historical_average ?? 0);
  const equityMaxValue = Math.max(equityNationalAverage, equityHistoricalAverage, equity);
  const equityMinValue = Math.min(equityNationalAverage, equityHistoricalAverage, equity);
  const equityMaxScaleFactor = equity / sliderMax >= 1 ? 1 : equity / sliderMax;
  const equityRangeFactor = sliderRangeCoEfficient - sliderRangeCoEfficient * equityMaxScaleFactor;
  const equitySliderMaxCoEfficient = 1 + equityRangeFactor;
  const equitySliderMinCoEfficient = 1 - equityRangeFactor;
  const equitySliderMax = equityMaxValue * equitySliderMaxCoEfficient;
  const equitySliderMin = equityMinValue * equitySliderMinCoEfficient;

  // Cash Flow slider settings
  // const dividendNationalAverage = Number(property?.sfr?.market_data.dividend_yield_national_average ?? 0);
  // const dividendHistoricalAverage = Number(property?.sfr?.market_data.dividend_yield_historical_average ?? 0);
  // const dividendMaxValue = Math.max(dividendNationalAverage, dividendHistoricalAverage, dividend);
  // const dividendMinValue = Math.min(dividendNationalAverage, dividendHistoricalAverage, dividend);
  // const dividendMaxScaleFactor = dividend / sliderMax >= 1 ? 1 : dividend / sliderMax;
  // const dividendRangeFactor = sliderRangeCoEfficient - sliderRangeCoEfficient * dividendMaxScaleFactor;
  // const dividendSliderMaxCoEfficient = 1 + dividendRangeFactor;
  // const dividendSliderMinCoEfficient = 1 - dividendRangeFactor;
  // const dividendSliderMax = dividendMaxValue * dividendSliderMaxCoEfficient;
  // const dividendSliderMin = dividendMinValue * dividendSliderMinCoEfficient;

  useEffect(() => {
    if (property) {
      setEquity(Number(property?.sfr?.market_data.projected_equity_rate ?? 0));
      setDividend(Number(property?.sfr?.market_data.projected_dividend_yield ?? 0));
    }
  }, [property]);

  // this is necessary to prevent the slider from triggering an auto-scroll on value change which is a current chakra slider bug (01/25/2023 v.^2.4.6)
  if (equity === 0 && dividend === 0) return null;

  return (
    <VStack alignItems={'flex-start'}>
      <Box mb={['0px', '40px']}>
        <SectionHeader text="Historical Returns in the Market" />
      </Box>
      {/* Equity Appreciation */}
      <Box w="100%">
        <Box justifyContent={'flex-start'} mb="20px">
          <TextBlock fontSize="20px" lineHeight={'24px'} color="gray.600">
            Equity Appreciation<sup style={{ fontSize: 11, marginLeft: 5 }}>†</sup>
          </TextBlock>
          <TextBlock fontSize="36px">
            <TextBlock fontSize="36px" as="span" weight="bold">
              {equity.toFixed(1)}%
            </TextBlock>
            / year
          </TextBlock>
        </Box>
        {equity > 0 && equityNationalAverage > 0 && equityHistoricalAverage > 0 && (
          <HStack mb="120px">
            <Slider
              value={equity}
              step={0.1}
              // onChange={setEquity}
              // onDoubleClick={() => setEquity(Number(property?.sfr?.market_data.projected_equity_rate ?? 0))}
              min={equitySliderMin}
              max={equitySliderMax}>
              <SliderMark
                value={equityNationalAverage}
                textAlign={'center'}
                mt="30px"
                alignItems={'center'}
                display="flex"
                flexDirection="column"
                justifyContent="center"
                marginLeft="-50px"
                width="100px">
                <Box
                  borderLeftWidth={'1px'}
                  borderLeftStyle="dashed"
                  borderLeftColor={'gray.400'}
                  height="36px"
                  width="1px"
                  marginBottom="8px"
                  zIndex={1}
                  backgroundColor="white"
                  borderRadius={'10px'}
                  marginTop="-48px"></Box>
                <Icon icon="triangle" color="gray.500" boxSize="16px" mb="2px" />
                <TextBlock weight="bold">{equityNationalAverage.toFixed(1)}%</TextBlock>
                <TextBlock color="gray.500" lineHeight="14.4px" fontSize="12px">
                  NATIONAL AVERAGE
                </TextBlock>
              </SliderMark>
              <SliderMark
                value={equityHistoricalAverage}
                textAlign={'center'}
                mt="30px"
                alignItems={'center'}
                display="flex"
                flexDirection="column"
                marginLeft="-50px"
                justifyContent="center"
                backgroundColor="white"
                borderRadius={'10px'}
                width="100px">
                <Box
                  borderLeftWidth={'1px'}
                  borderLeftStyle="dashed"
                  borderLeftColor={'gray.400'}
                  height="36px"
                  width="1px"
                  zIndex={1}
                  marginBottom="8px"
                  marginTop="-48px"></Box>
                <Icon icon="triangle" color="black" boxSize="16px" mb="2px" />
                <TextBlock weight="bold">{equityHistoricalAverage.toFixed(1)}%</TextBlock>
                <TextBlock color="gray.500" lineHeight="14.4px" fontSize="12px">
                  HISTORICAL AVERAGE
                </TextBlock>
              </SliderMark>
              <SliderTrack bg="blue.100" h="12px" borderRadius={'12px'}>
                <Box position="relative" right={10} />
                <SliderFilledTrack bg="blue.500" />
              </SliderTrack>
              <SliderThumb boxSize={6} />
            </Slider>
          </HStack>
        )}
      </Box>
      {/* TODO: Temporarily disabled Rental Dividend Yield */}
      {/* <Box w="100%">
        <Box justifyContent={'flex-start'} mb="20px">
         <TextBlock> fontSize="20px" lineHeight={'24px'} color="gray.600">
            Rental Dividend Yield
          </TextBlock>
         <TextBlock> fontSize="36px">
           <TextBlock> as="span" fontFamily={FontFamily.HELVETICA_NOW_BOLD}>
              {dividend.toFixed(1)}%
            </TextBlock>
            / year
          </TextBlock>
        </Box>
        {dividend > 0 && dividendNationalAverage > 0 && dividendHistoricalAverage > 0 && (
          <HStack mb="120px">
            <Slider
              value={dividend}
              step={0.1}
              // onChange={setDividend}
              // onDoubleClick={() => setDividend(Number(property?.sfr?.market_data.projected_dividend_yield ?? 0))}
              min={dividendSliderMin}
              max={dividendSliderMax}>
              <SliderMark
                value={dividendNationalAverage}
                textAlign={'center'}
                mt="30px"
                alignItems={'center'}
                display="flex"
                flexDirection="column"
                justifyContent="center"
                marginLeft="-50px"
                backgroundColor="white"
                borderRadius={'10px'}
                width="100px">
                <Box
                  borderLeftWidth={'1px'}
                  borderLeftStyle="dashed"
                  borderLeftColor={'gray.400'}
                  height="36px"
                  width="1px"
                  zIndex={1}
                  marginBottom="8px"
                  marginTop="-48px"></Box>
                <Icon icon="triangle" color="gray.500" boxSize="16px" mb="2px" />
               <TextBlock> weight='bold'>{dividendNationalAverage.toFixed(1)}%</TextBlock>
               <TextBlock> color="gray.500" lineHeight="14.4px" fontSize="12px">
                  NATIONAL AVERAGE
                </TextBlock>
              </SliderMark>
              <SliderMark
                value={dividendHistoricalAverage}
                textAlign={'center'}
                mt="30px"
                alignItems={'center'}
                display="flex"
                flexDirection="column"
                marginLeft="-50px"
                justifyContent="center"
                backgroundColor="white"
                borderRadius={'10px'}
                width="100px">
                <Box
                  borderLeftWidth={'1px'}
                  borderLeftStyle="dashed"
                  borderLeftColor={'gray.400'}
                  zIndex={1}
                  height="36px"
                  width="1px"
                  marginBottom="8px"
                  marginTop="-48px"></Box>
                <Icon icon="triangle" color="black" boxSize="16px" mb="2px" />
               <TextBlock> weight='bold'>{dividendHistoricalAverage.toFixed(1)}%</TextBlock>
               <TextBlock> color="gray.500" lineHeight="14.4px" fontSize="12px">
                  HISTORICAL AVERAGE
                </TextBlock>
              </SliderMark>
              <SliderTrack bg="green.100" h="12px" borderRadius={'12px'}>
                <Box position="relative" right={10} />
                <SliderFilledTrack bg="green.500" />
              </SliderTrack>
              <SliderThumb boxSize={6} />
            </Slider>
          </HStack>
        )}
      </Box> */}

      {/* Badly Hacked Together Custom Detail */}
      <CustomDetail
        propertyIds={[
          HACKED_TOGETHER_PROPERTY_IDS.dev.TheHartwell,
          HACKED_TOGETHER_PROPERTY_IDS.staging.TheDockery,
          HACKED_TOGETHER_PROPERTY_IDS.prod.TheDockery,
        ]}>
        <Box alignItems={'flex-start'} pb={'30px'} width="100%">
          <Box mb={['10px', '40px']}>
            <SectionHeader text="Rental Income" />
          </Box>
          <SimpleGrid
            columns={[1, 1, 1, 2]}
            templateColumns={['1fr', '1fr', '1fr', '1fr min-content']}
            gap={['10px', '10px', '10px', '23px']}
            padding={[0, 0, 0, '35px']}
            border={[0, 0, 0, '1px solid #F1F1F1']}
            borderRadius={[0, 0, 0, '10px']}
            fontSize={['16px', '16px', '16px', '20px']}
            width="100%">
            <GridItem>Annualized Rent ($2,490/mo) -</GridItem>
            <GridItem textAlign={'right'} fontFamily={FontFamily.HELVETICA_NOW_BOLD}>
              $29,880
            </GridItem>
            <GridItem>Operating, Financing, Legal, AUM Fee Expenses</GridItem>
            <GridItem textAlign={'right'}>-$15,770</GridItem>
            <GridItem colSpan={2}>
              <Divider />
            </GridItem>
            <GridItem>Cash Flow/Dividends</GridItem>
            <GridItem textAlign={'right'} fontFamily={FontFamily.HELVETICA_NOW_BOLD}>
              $14,110
            </GridItem>
            <GridItem>Raise Amount</GridItem>
            <GridItem textAlign={'right'} whiteSpace={'nowrap'}>
              ÷ $415,000
            </GridItem>
            <GridItem colSpan={2}>
              <Divider borderTopWidth={3} />
            </GridItem>
            <GridItem
              lineHeight={['24px', '24px', '24px', '36px']}
              fontFamily={FontFamily.HELVETICA_NOW_BOLD}
              fontSize={['18px', '18px', '18px', '24px']}>
              Rental Dividend Yield
            </GridItem>
            <GridItem
              lineHeight={['24px', '24px', '24px', '36px']}
              textAlign={'right'}
              fontFamily={FontFamily.HELVETICA_NOW_BOLD}
              fontSize={['24px', '24px', '24px', '36px']}>
              3.4%
            </GridItem>
          </SimpleGrid>
        </Box>
      </CustomDetail>

      {/* Hypothetical 1-Year Return */}
      <Box
        display="flex"
        flexDirection={['column', 'column', 'row']}
        padding="30px"
        borderRadius={'10px'}
        backgroundColor="#263243"
        justifyContent={'center'}
        alignItems={'center'}
        gap="18px">
        <VStack textAlign={'left'} alignItems="flex-start">
          <TextBlock
            color="white"
            fontSize="20px"
            fontFamily={FontFamily.HELVETICA_NOW_BOLD}
            style={{ margin: 0, padding: 0 }}>
            Hypothetical 1-Year Return<sup style={{ fontSize: 11, marginLeft: 5 }}>‡</sup>
          </TextBlock>
          <HStack>
            <TextBlock
              fontFamily={FontFamily.HELVETICA_NOW_BOLD}
              color="blue"
              background="linear-gradient(178deg, #63AAFF 0%, #63AAFF 30%, #C7E0FF 45%, #FFD698 60%, #C4A8FF 70%,#C4A8FF 100%)"
              backgroundClip="text"
              fontSize={['50px', '62px']}
              lineHeight={'90px'}
              style={{
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                margin: 0,
                padding: 0,
              }}>
              {(dividend + equity).toFixed(1)}%
            </TextBlock>
            <TextBlock color="gray.600" fontSize={['30px', '40px']} style={{ margin: 0, padding: 0 }}>
              /year
            </TextBlock>
          </HStack>

          <TextBlock color="gray.200" fontSize="12px" style={{ margin: 0, padding: 0 }}>
            Past performance does not guarantee future returns. Hypothetical returns are not inclusive of potential
            property disposition costs. For more information, refer to the Offering materials.
          </TextBlock>
        </VStack>
        <Image display={['none', 'none', 'none', 'hidden']} image="blue-residential-home-3qtr-angle" width={'200px'} />
      </Box>
    </VStack>
  );
};
